<script setup lang="ts">
import { computed, onUnmounted, ref, watch } from 'vue'
import {
  categoryRingColor, summarizeComponentScores, toRingSegments,
  type RawComponentScores,
} from '@/shared/utils/ringAdapter'

const props = withDefaults(defineProps<{
  scores: RawComponentScores
  /** When false, render the empty "no scores yet" state. */
  hasInput?: boolean
  /** Changes when a finished calculation should trigger the premium flourish. */
  celebrateKey?: number
}>(), { hasInput: true, celebrateKey: 0 })

// Geometry. SVG remains crisp at any device DPR.
const CENTER = 120
const R_RING = 104
const R_ARC = 80
const GAP_DEG = 14
const SPAN_DEG = (360 - 4 * GAP_DEG) / 4

let _uid = 0
const gradId = `hsr-grad-${++_uid}`

function polar(r: number, deg: number): [number, number] {
  const a = (deg - 90) * (Math.PI / 180)
  return [CENTER + r * Math.cos(a), CENTER + r * Math.sin(a)]
}

function arcPath(r: number, startDeg: number, endDeg: number): string {
  const [sx, sy] = polar(r, startDeg)
  const [ex, ey] = polar(r, endDeg)
  const large = endDeg - startDeg <= 180 ? 0 : 1
  return `M${sx.toFixed(2)} ${sy.toFixed(2)} A${r} ${r} 0 ${large} 1 ${ex.toFixed(2)} ${ey.toFixed(2)}`
}

const segments = computed(() =>
  toRingSegments(props.scores).map((seg, i) => {
    const start = i * 90 + GAP_DEG / 2
    return { ...seg, d: arcPath(R_ARC, start, start + SPAN_DEG) }
  }),
)

const summary = computed(() => summarizeComponentScores(props.scores))
const outerColor = computed(() => categoryRingColor(summary.value.category))
const showScore = computed(() => props.hasInput && summary.value.composite !== null)
const outerPct = computed(() => showScore.value ? Math.max(0, Math.min(100, summary.value.composite ?? 0)) : 0)
const bigNumber = computed(() => {
  const c = summary.value.composite
  return c === null ? '--' : c.toFixed(1)
})

const ariaSummary = computed(() => {
  if (!showScore.value) return 'Readiness ring: no scores entered yet.'
  const parts = segments.value
    .map(s => `${s.label} ${s.exempt ? 'exempt' : (s.raw ?? 0).toFixed(1) + ' of ' + s.max}`)
    .join(', ')
  return `Readiness composite ${bigNumber.value} of 100, ${summary.value.category}. ${parts}.`
})

const isCelebrating = ref(false)
let celebrateTimer: number | undefined

watch(() => props.celebrateKey, value => {
  if (!value || !showScore.value) return
  if (celebrateTimer) window.clearTimeout(celebrateTimer)
  isCelebrating.value = false
  window.requestAnimationFrame(() => {
    isCelebrating.value = true
    celebrateTimer = window.setTimeout(() => {
      isCelebrating.value = false
    }, 900)
  })
})

onUnmounted(() => {
  if (celebrateTimer) window.clearTimeout(celebrateTimer)
})
</script>

<template>
  <div class="hero-ring" :class="{ 'is-celebrating': isCelebrating }" role="img" :aria-label="ariaSummary">
    <div class="hero-ring__stage">
      <svg viewBox="0 0 240 240" class="hero-ring__svg" aria-hidden="true">
        <defs>
          <linearGradient :id="gradId" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" :stop-color="outerColor" />
            <stop offset="50%" stop-color="#a07840" />
            <stop offset="100%" :stop-color="outerColor" />
          </linearGradient>
        </defs>

        <circle :cx="CENTER" :cy="CENTER" :r="R_RING" fill="none"
                stroke="var(--border2)" stroke-width="16" opacity="0.3" />
        <circle v-if="showScore" :cx="CENTER" :cy="CENTER" :r="R_RING + 17" fill="none"
                :stroke="outerColor" stroke-width="1.5" opacity="0"
                class="hero-ring__halo" />
        <circle v-if="showScore" :cx="CENTER" :cy="CENTER" :r="R_RING" fill="none"
                :stroke="`url(#${gradId})`" stroke-width="13" stroke-linecap="round"
                pathLength="100" class="hero-ring__outer-value"
                transform="rotate(-90 120 120)"
                :style="{ strokeDashoffset: 100 - outerPct }" />
        <circle v-if="showScore" :cx="CENTER" :cy="CENTER" :r="R_RING + 8" fill="none"
                :stroke="outerColor" stroke-width="2" opacity="0.35"
                pathLength="100" class="hero-ring__outer-value"
                transform="rotate(-90 120 120)"
                :style="{ strokeDashoffset: 100 - outerPct }" />

        <template v-for="seg in segments" :key="seg.key">
          <path :d="seg.d" fill="none" stroke="var(--border2)" stroke-width="12"
                stroke-linecap="round" opacity="0.4" />
          <path v-if="!seg.exempt" :d="seg.d" fill="none" :stroke="seg.color"
                stroke-width="12" stroke-linecap="round"
                pathLength="100" class="hero-ring__value"
                :style="{ strokeDashoffset: 100 - (seg.pct ?? 0) }" />
        </template>
      </svg>

      <div class="hero-ring__center">
        <template v-if="showScore">
          <div class="hero-ring__score">{{ bigNumber }}</div>
          <div class="hero-ring__cat" :style="{ color: outerColor }">{{ summary.category }}</div>
          <div class="hero-ring__unit">composite / 100</div>
        </template>
        <template v-else>
          <div class="hero-ring__score muted">--</div>
          <div class="hero-ring__hint">Enter your scores</div>
        </template>
      </div>
    </div>

    <ul class="hero-ring__legend">
      <li v-for="seg in segments" :key="seg.key">
        <span class="dot" :style="{ background: seg.color }" />
        <span class="ll">{{ seg.label }}</span>
        <span class="lv">{{ seg.exempt ? 'EXM' : (seg.raw ?? 0).toFixed(1) + ' / ' + seg.max }}</span>
      </li>
    </ul>

    <p class="hero-ring__sr">{{ ariaSummary }}</p>
  </div>
</template>

<style scoped>
.hero-ring {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
}
.hero-ring__stage {
  position: relative;
  width: 100%;
  max-width: 260px;
}
.hero-ring__svg {
  display: block;
  width: 100%;
  height: auto;
}
.hero-ring__outer-value,
.hero-ring__value {
  stroke-dasharray: 100;
  transition: stroke-dashoffset 0.45s cubic-bezier(0.22, 1, 0.36, 1);
}
.hero-ring.is-celebrating .hero-ring__outer-value {
  animation: hero-ring-draw 0.75s cubic-bezier(0.19, 1, 0.22, 1), hero-ring-glow 0.9s ease-out;
}
.hero-ring.is-celebrating .hero-ring__value {
  animation: hero-ring-draw 0.62s cubic-bezier(0.19, 1, 0.22, 1);
}
.hero-ring.is-celebrating .hero-ring__halo {
  animation: hero-ring-halo 0.85s ease-out;
}
.hero-ring__center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}
.hero-ring__score {
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 800;
  font-size: 50px;
  line-height: 1;
  color: var(--text);
}
.hero-ring.is-celebrating .hero-ring__center {
  animation: hero-ring-center-pop 0.55s cubic-bezier(0.22, 1, 0.36, 1);
}
.hero-ring__score.muted { color: var(--muted); }
.hero-ring__cat {
  font-family: 'Barlow Condensed', sans-serif;
  font-weight: 700;
  font-size: 14px;
  letter-spacing: 2px;
  text-transform: uppercase;
  margin-top: 4px;
}
.hero-ring__unit,
.hero-ring__hint {
  font-size: 10px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--muted);
  margin-top: 4px;
}
.hero-ring__legend {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px 18px;
  width: 100%;
  max-width: 280px;
}
.hero-ring__legend li {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: var(--text);
}
.hero-ring__legend .dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.hero-ring__legend .ll { color: var(--muted); }
.hero-ring__legend .lv { margin-left: auto; font-variant-numeric: tabular-nums; }
.hero-ring__sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
@media (prefers-reduced-motion: reduce) {
  .hero-ring__outer-value,
  .hero-ring__value { transition: none; }
  .hero-ring.is-celebrating .hero-ring__outer-value,
  .hero-ring.is-celebrating .hero-ring__value,
  .hero-ring.is-celebrating .hero-ring__halo,
  .hero-ring.is-celebrating .hero-ring__center { animation: none; }
}

@keyframes hero-ring-draw {
  from { stroke-dashoffset: 100; }
}
@keyframes hero-ring-glow {
  0% { filter: drop-shadow(0 0 0 transparent); }
  38% { filter: drop-shadow(0 0 14px rgba(201, 154, 73, 0.38)); }
  100% { filter: drop-shadow(0 0 0 transparent); }
}
@keyframes hero-ring-halo {
  0% {
    opacity: 0.55;
    transform: scale(0.94);
    transform-origin: 120px 120px;
  }
  100% {
    opacity: 0;
    transform: scale(1.06);
    transform-origin: 120px 120px;
  }
}
@keyframes hero-ring-center-pop {
  0% { transform: scale(0.97); opacity: 0.82; }
  52% { transform: scale(1.035); opacity: 1; }
  100% { transform: scale(1); opacity: 1; }
}
</style>
